// How a chat card is applied (moved out of SourcingChatPanel.js by Lane 1368 so the
// routes can be checked without a browser; no change for the cards that existed). Every
// card goes through the same manual route as its button, with the card id as the
// request key, so it applies at most once.
// Each prepared change goes through the same manual route as the buttons.
// The proposal id is the request key, so it is applied at most once.
const PROPOSAL_ROUTES = {
  rename_event: (i) => ['PATCH', '', { name: i.name }],
  remove_lines: (i) => ['POST', '/lines/remove', { selection: i.selection }],
  set_quantity: (i) => ['POST', '/lines/quantity', { items: i.items }],
  set_step: (i) => ['POST', '/lines/step', { items: i.items }],
  set_incumbent: (i) => ['POST', '/lines/incumbent', { selection: i.selection, supplier_id: i.supplier_id }],
  set_terms: (i) => ['PUT', '/terms', { terms: i.terms }],
  set_qualification: (i) => ['PUT', '/qualifications', {
    material_number: i.material_number, supplier_id: i.supplier_id, state: i.state, evidence: i.evidence }],
  clear_qualification: (i) => ['POST', '/qualifications/clear', {
    material_number: i.material_number, supplier_id: i.supplier_id }],
  // Lane 1368 (plan B3.8): the goal in the buyer's own words, checked when prepared.
  set_goal: (i) => ['POST', '/goal', { goal: i.goal === undefined ? null : i.goal }],
};

// Lane 1368 (slice S7): every other change a buyer makes by hand, prepared in chat and
// applied here through the same manual route as its button. The server wrote and
// checked the input (including the digests the screens carry); the card id is the
// request key, so each card applies at most once.
export const CONTROL_ROUTES = {
  // The card carries the time zone it was checked in (the buyer's, sent with the message).
  set_schedule: (i) => ['POST', '/rounds/set_schedule', i],
  extend_due_date: (i) => ['POST', '/rounds/extend_due_date', i],
  close_round: (i) => ['POST', '/rounds/close_round', i],
  open_round: (i) => ['POST', '/rounds/open_round', i],
  release_disclosure: (i) => ['POST', '/rounds/release_disclosure', i],
  cancel_event: () => ['POST', '/cancel', {}],
  publish: (i) => ['POST', '/publish', i],
  edit_note: (i) => ['PATCH', `/notes/${Number(i.note_id)}`, { body: i.body }],
  set_line_supplier_note: (i) => ['POST', '/lines/supplier-note', i],
  invite_contact: (i) => ['POST', '/invitations/invite_contact', i],
  reissue_invitation: (i) => ['POST', '/invitations/reissue_invitation', i],
  revoke_invitation: (i) => ['POST', '/invitations/revoke_invitation', i],
  revoke_supplier: (i) => ['POST', '/invitations/revoke_supplier', i],
  create_feedback: (i) => ['POST', '/disclosures/create_feedback', i],
  update_feedback: (i) => ['POST', '/disclosures/update_feedback', i],
  discard_feedback: (i) => ['POST', '/disclosures/discard_feedback', i],
  discard_amendment: () => ['POST', '/amendments/discard', {}],
  publish_amendment: (i) => ['POST', '/amendments/publish', i],
  remove_document: (i) => ['POST', `/documents/${encodeURIComponent(i.document_id)}/remove`, {}],
  set_document_flags: (i) => ['POST', `/documents/${encodeURIComponent(i.document_id)}/flags`,
    { supplier_visible: i.supplier_visible, informational: i.informational }],
  confirm_rule: (i) => ['POST', '/rules/confirm', i],
  copy_scenario: (i) => ['POST', '/scenarios/copy', i],
  archive_scenario: (i) => ['POST', '/scenarios/archive', i],
  unmark_preferred: (i) => ['POST', '/scenarios/unprefer', i],
  move_lines: (i) => ['POST', '/scenarios/move-lines', i],
  set_negotiation_target: (i) => ['POST', '/negotiation/targets', i],
  dismiss_cleared_targets: (i) => ['POST', '/negotiation/targets/dismiss-cleared', i],
  add_targets_to_feedback: (i) => ['POST', '/negotiation/feedback', i],
  build_negotiation_deck: (i) => ['POST', '/negotiation/deck', i],
};
// Lane 1368 (slice S7b): on a published event these three edits, saved at once on a
// draft, come as cards for the change in progress, applied through their manual routes
// (which edit the change in progress). remove_lines, set_quantity and set_terms use
// PROPOSAL_ROUTES above.
export const AMENDMENT_ROUTES = {
  add_lines: (i) => ['POST', '/lines', i.conditions && i.conditions.length
    ? { filters: i.filters, conditions: i.conditions } : { filters: i.filters }],
  exclude_lines: (i) => ['POST', '/lines/exclude', { selection: i.selection, reason: i.reason }],
  include_lines: (i) => ['POST', '/lines/include', { selection: i.selection }],
  // Lane 1408: the card froze the lines when shown: the smaller list plus the newest
  // line then (as_of_line_id), so a line added before the click is never touched.
  keep_only_lines: (i) => ['POST', '/lines/keep-only', i.leave_out_line_uids
    ? { reason: i.reason, leave_out_line_uids: i.leave_out_line_uids, as_of_line_id: i.as_of_line_id }
    : { selection: i.selection, reason: i.reason, as_of_line_id: i.as_of_line_id }],
};

export const CONTROL_ACTIONS = Object.keys(CONTROL_ROUTES).concat(['delete_note']);

// Rule and scenario changes (slice 3D) carry their own expected version.
const ANALYSIS_ROUTES = {
  edit_rule: (api, id, i, key) => api.ruleAction(id, 'edit', i, key),
  reject_rule: (api, id, i, key) => api.ruleAction(id, 'reject', { rule_id: i.rule_id, expected_version: i.expected_version }, key),
  edit_scenario: (api, id, i, key) => api.scenarioAction(id, 'edit', i, key),
  // Slice 2b: the scenario page is read only; these two come as chat cards.
  use_current_rules: (api, id, i, key) => api.scenarioAction(id, 'use-current-rules', i, key),
  restore_scenario_revision: (api, id, i, key) => api.scenarioAction(id, 'restore', i, key),
};

// Lane 1375 (Ryan, R4): the "add suppliers" card offers one button per choice; the
// click adds only the chosen suppliers, in one change, with the card id as the key (a
// second click, with any choice, is refused as already applied).
export function applyProposal(api, eventId, card, revision, choice) {
  const input = card.input || {};
  if (card.action === 'add_suppliers') {
    if (!choice || !Array.isArray(choice.supplier_ids)) return Promise.reject(new Error('Pick which suppliers to add.'));
    return api.act(eventId, 'POST', '/suppliers/bulk-add', { supplier_ids: choice.supplier_ids }, revision,
      card.proposal_id);
  }
  if (card.action === 'delete_note') {
    return api.actQuery(eventId, 'DELETE', `/notes/${Number(input.note_id)}`, revision, card.proposal_id);
  }
  if (AMENDMENT_ROUTES[card.action]) {
    const [aMethod, aPath, aBody] = AMENDMENT_ROUTES[card.action](input);
    return api.act(eventId, aMethod, aPath, aBody, revision, card.proposal_id);
  }
  if (CONTROL_ROUTES[card.action]) {
    const [cMethod, cPath, cBody] = CONTROL_ROUTES[card.action](input);
    // A record or an action with its own version check gets no event revision.
    return api.act(eventId, cMethod, cPath, cBody, card.revision_seen == null ? undefined : revision,
      card.proposal_id);
  }
  if (ANALYSIS_ROUTES[card.action]) return ANALYSIS_ROUTES[card.action](api, eventId, input, card.proposal_id);
  if (card.action === 'invite_supplier') {
    // It sends an invitation: the card's id is the key, so it goes at most once.
    return api.invitationAction(eventId, 'invite_supplier', input, revision, card.proposal_id);
  }
  if (card.action === 'remove_supplier') {
    return api.actQuery(eventId, 'DELETE', `/suppliers/${Number(input.supplier_id)}`, revision,
      card.proposal_id);
  }
  const route = PROPOSAL_ROUTES[card.action];
  if (!route) return Promise.reject(new Error('This change cannot be applied here.'));
  const [method, path, body] = route(input);
  return api.act(eventId, method, path, body, revision, card.proposal_id);
}
